import Link from "next/link";
import { tv, type VariantProps } from "tailwind-variants";
import { forwardRef, type ButtonHTMLAttributes } from "react";

// ref: https://dev.to/teyim/create-reusable-button-components-with-reacttypescript-tailwind-and-tailwind-variants-2j7d

type BaseButtonAttributes = ButtonHTMLAttributes<HTMLButtonElement | HTMLAnchorElement>;
export interface ButtonProps
  extends BaseButtonAttributes,
    VariantProps<typeof buttonStyles> {
  href?: string;
  icon?: any;
  leftIcon?: boolean;
  position?: "start" | "center" | "end";
  newTab?: boolean;
  nogap?: boolean;
  mail?: boolean;
  tel?: boolean;
}

const buttonStyles = tv({
  base: "text-white border-2 text-center rounded-full",
  variants: {
    variant: {
      primary: "bg-orange border-white hover:bg-orange/80",
      secondary: "bg-green border-white hover:bg-green/80",
      outline: "border-green hover:bg-black/15",
      textLink: "border-none hover:text-orange",
    },
    size: {
      default: "h-auto px-4 py-2",
      xs: " px-2.5 py-0.5 text-xs",
      sm: "h-auto px-3.5 py-1 text-base",
      "sm-long": "h-auto px-6 py-1 text-base",
      lg: "text-lg px-4 py-2",
      "lg-flat": "text-lg px-4 py-1",
      none: "h-auto",
    },
  },
  defaultVariants: {
    variant: "primary",
    size: "default",
  },
});

const CustomButton = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      children,
      href,
      icon,
      leftIcon,
      onClick,
      ...props
    },
    ref,
  ) => {
    if (href) {
      return (
        <Link
          href={`${props.mail ? `mailto:${href}` : props.tel ? `tel:${href}` : href}`}
          className={`${buttonStyles({ variant, size, className })} flex items-center ${props.position === "start" ? "justify-start" : props.position === "end" ? "justify-end" : "justify-center"} ${props.nogap ? "gap-0" : "gap-1"}`}
          target={props.newTab ? "_blank" : ""}
          onClick={onClick}
        >
          {children}
        </Link>
      );
    }

    return (
      <button
        className={`${buttonStyles({ variant, size, className })} flex items-center justify-center ${props.nogap ? "gap-0" : "gap-1"}`}
        ref={ref}
        onClick={onClick}
        {...props}
      >
        {children}
      </button>
    );
  },
);

CustomButton.displayName = "CustomButton";

export default CustomButton;
